<template lang="pug">
    Tabs.category-list
        el-card.contains-table( shadow="always" )
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .categories-table(v-else)
                // :row-link-config="categoriesPaginated.link"
                paginated-items(
                    scope="ex"
                    type="evnt_category"
                    :loading="loading"
                    :items="categoriesPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="categoriesPaginated.variables"
                    :all-columns="categoriesPaginated.allColumns"
                    :active-columns="categoriesPaginated.activeColumns"
                    :page-info="categoriesPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    div(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addCategory")
                                    | {{ $t('New Category') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
</template>

<i18n>
{
  "de": {
    "New Category": "Neue Kategorie",
    "Show filters": "Filter anzeigen",
    "Hide filters": "Filter verbergen",
    "Category list could not be fetched.": "Kategorieliste konnte nicht abgerufen werden."
  }
}
</i18n>

<script>
import _ from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo'
import { useSetupStore, useEventManagerCategoryStore } from '@/stores'

import router from '@/router'
import pinia from '@/pinia';

import PaginatedItems from '@/components/PaginatedItems'
import Tabs from '@/extensions/eventManager/views/Partials/Tabs'
import Error from '@/components/Error';

export default {
    name: 'Categories',
    data() {
        return {
            error: false,
            disable: {
                showFilters: false,
            }
        }
    },
    components: {
        PaginatedItems,
        Error,
        Tabs
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) this.initialize()
        },
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.categoriesPaginated, 'showFilters') || false
        },
        hasFilters() {
            return ! _.isEmpty(this.filters)
        },
        filters() {
            let filters = _.cloneDeep(this.categoriesPaginated.filters)
            delete filters.cms
            return filters
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        debounce() {
            return useSetupStore().debounce;
        },
        categoryStore() {
            return useEventManagerCategoryStore();
        },
        loading() {
            return this.categoryStore.loading;
        },
        categoriesPaginated() {
            return this.categoryStore.categoriesPaginated;
        },
        sort() {
            var variables = this.categoriesPaginated.variables;
            var defaultSort = this.categoriesPaginated.defaultSort || { field: 'id', direction: 'asc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        }
    },
    methods: {
        initialize() {
            this.updateLoading(true)
            this.refetch()
        },
        addCategory() {
            const params = { id: 'new' }
            router.push({ name: 'event-category-main', params })
        },
        handleClick(item) {
            const params = { id: item.id }
            router.push({ name: 'event-category-main', params })
        },
        handleSizeChange(itemsPerPage) {
            this.updateLoading(true)
            this.defineItemsPerPage(itemsPerPage)
            this.refetch()
        },
        handleCurrentChange (currentPage) {
            this.updateLoading(true)
            this.defineCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns)
            // apolloClient.cache.reset()
            apolloClient.clearStore()
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineFilterValue({ key, value })
            if (debounce) this.debouncedRefetch()
            else this.refetch()
        },
        handleSortInputChange(key, value) {
            this.categoryStore.defineSorting({ key, value })
            this.refetch()
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (! visible) value = true

            this.disable.showFilters = true
            this.defineFilterVisibility(value)
            this.saveFilterVisibility({ scope: 'ex', key: 'evnt_category', value })
                .finally(() => this.disable.showFilters = false)
        },
        refetch() {

            // always add the cms id to filters
            const cmsID = this.activeCms.id
            const filter = {
                key: 'cms',
                match: cmsID,
            }

            this.defineFilterValue({ key: 'cms', value: filter })

            this.fetchCategoriesPaginated()
                .then(() => this.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Category list could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.updateLoading(false);

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
        },
        debouncedRefetch: _.debounce(function() { // function scope is needed here
            this.updateLoading(true)
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
        activateTab(item) {
            return useSetupStore().activateTab(item)
        },
        fetchCategoriesPaginated() {
            return this.categoryStore.fetchCategoriesPaginated();
        },
        updateLoading(value) {
            this.categoryStore.updateLoading(value);
        },
        defineItemsPerPage(itemsPerPage) {
            this.categoryStore.defineItemsPerPage(itemsPerPage);
        },
        defineCurrentPage(currentPage) {
            this.categoryStore.defineCurrentPage(currentPage);
        },
        defineActiveColumns(visibleColumns) {
            this.categoryStore.defineActiveColumns(visibleColumns);
        },
        defineFilterValue(payload) {
            this.categoryStore.defineFilterValue(payload);
        },
        defineFilterVisibility(value) {
            this.categoryStore.defineFilterVisibility(value);
        },
    },
    mounted() {
        this.initialize()
    },
}
</script>

<style lang="sass">
    .error
        padding: 20px
</style>
